import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { WeeklyEntry } from '../api/useNotificationMetrics';

interface WeeklyTrendChartProps {
  weekly: WeeklyEntry[];
  loading: boolean;
}

function formatWeekLabel(weekStart: string): string {
  const date = new Date(weekStart + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function WeeklyTrendChart({ weekly, loading }: WeeklyTrendChartProps): React.ReactElement {
  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="h-5 w-40 bg-gray-800 rounded animate-pulse mb-4" />
        <div className="h-64 bg-gray-800/40 rounded-xl animate-pulse" />
      </div>
    );
  }

  const chartData = [...weekly]
    .reverse()
    .map((entry) => ({
      week: formatWeekLabel(entry.week_start),
      Sent: entry.total_sent,
      Opened: entry.total_opened,
      'Attributed Visits': entry.attributed_visits,
    }));

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-white mb-4 tracking-tight">Weekly Trend</h3>
      {chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center">
          <p className="text-sm text-gray-500">No weekly data available</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} barGap={2} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={{ stroke: '#374151' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '12px',
                fontSize: '13px',
              }}
              labelStyle={{ color: '#d1d5db' }}
              itemStyle={{ color: '#9ca3af' }}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }}
            />
            <Bar dataKey="Sent" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Opened" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Attributed Visits" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default WeeklyTrendChart;
